/**
 * LegAnalysisDetails Component
 * Expanded detailed analysis for a single leg
 */

import type { GeneratedLeg } from '@parlay-picker/shared/api/picksApi';
import { legTextWithMarket } from '@parlay-picker/shared/statLabels';
import type React from 'react';
import { formatPropDisplay } from '../utils';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Determine blowout risk level from spread value
 * High: |spread| >= 10 (one team heavily favored)
 * Medium: |spread| >= 7
 * Low/None: |spread| < 7
 */
function getBlowoutRisk(spread: number | null | undefined): 'high' | 'medium' | 'low' | null {
  if (spread == null) return null;
  const absSpread = Math.abs(spread);
  if (absSpread >= 10) return 'high';
  if (absSpread >= 7) return 'medium';
  return 'low';
}

// =============================================================================
// Types
// =============================================================================

interface LegAnalysisDetailsProps {
  leg: GeneratedLeg;
}

// =============================================================================
// Component
// =============================================================================

export function LegAnalysisDetails({ leg }: LegAnalysisDetailsProps): React.ReactElement {
  const propDisplay = legTextWithMarket(leg.prop ?? '', leg.marketKey);
  const explanation = leg.explanation;
  const analysis = leg.analysis;

  // Extract blowout risk from betContext spread
  // Contexts stored before the backlog 540 rename hold only the old key.
  const betCtx = leg.betContext as
    | { gameContext?: { homeSpread?: number | null; spread?: number | null } }
    | null
    | undefined;
  const spread = betCtx?.gameContext?.homeSpread ?? betCtx?.gameContext?.spread;
  const blowoutRisk = getBlowoutRisk(spread);

  return (
    <div className="bg-theme-bg/50 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-theme-text text-sm">
          {leg.player || leg.team || 'Team Bet'}
        </span>
        <span className="text-xs px-2 py-0.5 rounded bg-theme-surface text-theme-text">
          {formatPropDisplay(propDisplay)}
        </span>
      </div>
      <p className="text-theme-text-tertiary text-xs mb-2 italic">
        AI-generated analysis - may contain errors
      </p>

      {/* Primary Edge Box */}
      {explanation?.primaryEdge && (
        <div className="bg-theme-primary/15 border border-theme-primary/30 rounded-lg p-2 mb-2">
          <div className="text-xs font-semibold text-theme-primary uppercase tracking-wider mb-1">
            Primary Edge
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1">
              <span className="text-theme-text-secondary">
                {explanation.primaryEdge.metric} Projected:
              </span>
              <span className="text-theme-text font-medium">
                {explanation.primaryEdge.playerValue}
              </span>
            </div>
            <span className="text-theme-text-tertiary">|</span>
            <div className="flex items-center gap-1">
              <span className="text-theme-text-secondary">Line:</span>
              <span className="text-theme-text font-medium">{explanation.primaryEdge.line}</span>
            </div>
            <span className="text-theme-text-tertiary">|</span>
            <div className="flex items-center gap-1">
              <span className="text-theme-text-secondary">Edge:</span>
              <span
                className={`font-bold ${(explanation.primaryEdge.edge || 0) > 0 ? 'text-theme-success' : 'text-theme-danger'}`}
              >
                {(explanation.primaryEdge.edge || 0) > 0 ? '+' : ''}
                {explanation.primaryEdge.edge}
              </span>
              <span className="text-theme-text-tertiary text-xs">
                ({(explanation.primaryEdge.edgePercent || 0) > 0 ? '+' : ''}
                {explanation.primaryEdge.edgePercent?.toFixed(1)}%)
              </span>
            </div>
          </div>
          {explanation.hitRate && (
            <div className="text-xs text-theme-text-secondary mt-1">
              {explanation.hitRate.description}
            </div>
          )}
        </div>
      )}

      {/* Supporting Factors */}
      {/* TODO: Switch to a 3-column grid layout (factor, value, description) for mobile viewport
       * when any supporting factor text wraps beyond 2 lines. Test across screen sizes (320px–768px).
       * Current inline layout breaks readability on narrow screens with long factor descriptions. */}
      {explanation?.supportingFactors && explanation.supportingFactors.length > 0 && (
        <div className="bg-theme-success/15 border border-theme-success/30 rounded-lg p-2 mb-2">
          <div className="text-xs font-semibold text-theme-success uppercase tracking-wider mb-1">
            Supporting Factors
          </div>
          <div className="space-y-1">
            {explanation.supportingFactors.map((factor, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    factor.impact === 'positive'
                      ? 'bg-theme-success'
                      : factor.impact === 'negative'
                        ? 'bg-theme-danger'
                        : 'bg-theme-text-secondary'
                  }`}
                />
                <span className="text-theme-text">{factor.label}:</span>
                <span className="text-theme-text font-medium">{factor.value}</span>
                {factor.detail && (
                  <span className="text-theme-text-tertiary">({factor.detail})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Factors */}
      {explanation?.riskFactors && explanation.riskFactors.length > 0 && (
        <div className="bg-theme-warning/15 border border-theme-warning/30 rounded-lg p-2 mb-2">
          <div className="text-xs font-semibold text-theme-warning uppercase tracking-wider mb-1">
            Risk Factors
          </div>
          <div className="space-y-1">
            {explanation.riskFactors.map((risk, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                <span
                  className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    risk.severity === 'high'
                      ? 'bg-theme-danger/15 text-theme-danger'
                      : risk.severity === 'medium'
                        ? 'bg-theme-warning/15 text-theme-warning'
                        : 'bg-theme-surface text-theme-text-secondary'
                  }`}
                >
                  {risk.severity}
                </span>
                <span className="text-theme-text">{risk.label}:</span>
                <span className="text-theme-text-secondary">{risk.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confidence Breakdown */}
      {explanation?.confidenceBreakdown && (
        <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-2 mb-2">
          <div className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-2">
            Confidence Breakdown
          </div>
          <div className="space-y-1.5">
            {[
              {
                label: 'Statistical Edge',
                value: explanation.confidenceBreakdown.statisticalEdge,
              },
              {
                label: 'Matchup Factor',
                value: explanation.confidenceBreakdown.matchupFactor,
              },
              {
                label: 'Consistency',
                value: explanation.confidenceBreakdown.consistency,
              },
              {
                label: 'Game Script',
                value: explanation.confidenceBreakdown.gameScript,
              },
            ].map((factor) => (
              <div key={factor.label} className="flex items-center gap-2 text-xs">
                <span className="text-theme-text-secondary w-24 shrink-0">{factor.label}:</span>
                <div className="flex-1 h-2 bg-theme-surface rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      (factor.value || 0) >= 0.7
                        ? 'bg-theme-success'
                        : (factor.value || 0) >= 0.5
                          ? 'bg-theme-warning'
                          : 'bg-theme-danger'
                    }`}
                    style={{ width: `${(factor.value || 0) * 100}%` }}
                  />
                </div>
                <span className="text-theme-text w-10 text-right">
                  {Math.round((factor.value || 0) * 100)}%
                </span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-xs pt-1 border-t border-purple-500/30 mt-1">
              <span className="text-purple-400 font-medium">Overall:</span>
              <span
                className={`font-bold ${
                  (explanation.confidenceBreakdown.overall || 0) >= 0.7
                    ? 'text-theme-success'
                    : (explanation.confidenceBreakdown.overall || 0) >= 0.5
                      ? 'text-theme-warning'
                      : 'text-theme-danger'
                }`}
              >
                {Math.round((explanation.confidenceBreakdown.overall || 0) * 100)}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Blowout Risk Alert (shown when high/medium, regardless of analysis presence) */}
      {blowoutRisk && blowoutRisk !== 'low' && !analysis && (
        <div
          className={`rounded-lg p-2 mb-2 ${
            blowoutRisk === 'high'
              ? 'bg-theme-danger/15 border border-theme-danger/30'
              : 'bg-theme-warning/15 border border-theme-warning/30'
          }`}
        >
          <div
            className={`text-xs font-semibold uppercase tracking-wider mb-1 ${
              blowoutRisk === 'high' ? 'text-theme-danger' : 'text-theme-warning'
            }`}
          >
            {blowoutRisk === 'high' ? '⚠️ High Blowout Risk' : '📊 Medium Blowout Risk'}
          </div>
          <div className="text-xs text-theme-text">
            {blowoutRisk === 'high'
              ? `Spread: ${spread}. Starters may rest in blowouts. Unders historically hit 62% in high blowout games.`
              : `Spread: ${spread}. Game could become lopsided, affecting minutes.`}
          </div>
        </div>
      )}

      {/* Legacy Analysis Data */}
      {analysis ? (
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-theme-text-tertiary">Line:</span>
            <span className="text-theme-text">{formatPropDisplay(propDisplay)}</span>
            <span className="text-theme-text-tertiary mx-1">|</span>
            <span className="text-theme-success font-medium">{analysis.average}</span>
            {analysis.trend && (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium cursor-help ${
                  analysis.trend === 'Hot'
                    ? 'bg-orange-500/15 text-orange-400'
                    : analysis.trend === 'Cold'
                      ? 'bg-theme-primary/15 text-theme-primary'
                      : 'bg-theme-surface text-theme-text-secondary'
                }`}
                title={
                  analysis.trend === 'Hot'
                    ? 'Hot: Player trending up (L5 > L10 > L20). Recent performance exceeds season average.'
                    : analysis.trend === 'Cold'
                      ? 'Cold: Player trending down (L5 < L10 < L20). Recent performance below season average.'
                      : 'Consistent: Stable performance across all time periods (L5 ≈ L10 ≈ L20).'
                }
              >
                {analysis.trend === 'Hot'
                  ? '🔥 Hot'
                  : analysis.trend === 'Cold'
                    ? '❄️ Cold'
                    : '➡️ Consistent'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium ${
                analysis.edge === 'Strong'
                  ? 'bg-theme-success/15 text-theme-success'
                  : analysis.edge === 'Moderate'
                    ? 'bg-theme-warning/15 text-theme-warning'
                    : 'bg-theme-surface text-theme-text-secondary'
              }`}
            >
              {analysis.edge} Edge
            </span>
            {blowoutRisk && blowoutRisk !== 'low' && (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium cursor-help ${
                  blowoutRisk === 'high'
                    ? 'bg-theme-danger/15 text-theme-danger'
                    : 'bg-theme-warning/15 text-theme-warning'
                }`}
                title={
                  blowoutRisk === 'high'
                    ? `High blowout risk (spread: ${spread}). Starters may rest in blowouts. Unders historically hit 62% in high blowout games.`
                    : `Medium blowout risk (spread: ${spread}). Game could become lopsided.`
                }
              >
                {blowoutRisk === 'high' ? '⚠️ Blowout Risk' : '📊 Blowout Risk'}
              </span>
            )}
            <span className="text-theme-text-secondary">{analysis.reason}</span>
          </div>
          {analysis.profileNote && (
            <div className="flex items-center gap-2 pt-1 border-t border-theme-border/50">
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-500/15 text-purple-400">
                Your History
              </span>
              <span className="text-purple-400">{analysis.profileNote}</span>
            </div>
          )}
        </div>
      ) : (
        !explanation?.primaryEdge && (
          <p className="text-xs text-theme-text-tertiary">
            Statistical analysis not available for this leg
          </p>
        )
      )}
    </div>
  );
}
